/* eslint-disable @typescript-eslint/no-explicit-any */
import { oc } from 'ts-optchain';
import _ from 'lodash';

import { ExtendedService } from 'types';
import { allWeekdayStrings } from '@tock/shared/utils/time';
import { core, toJSON } from '@tock/coretypes';
import AppConstants from 'constants/AppConstants';
import EntityStateConstants from 'constants/EntityStateConstants';
import SaveButtonConstants from 'constants/SaveButtonConstants';

export interface ServiceWeekdayMap {
  [id: string]: ExtendedService[];
}

interface State {
  saveButtonState: string;
  forWeekday: ServiceWeekdayMap;
  isInitialized: boolean;
  requestInProgress: boolean;
}

const initialState: State = {
  saveButtonState: SaveButtonConstants.SAVED,
  forWeekday: _.fromPairs(_.map(allWeekdayStrings(), (day) => [day, []])),
  isInitialized: false,
  requestInProgress: false,
};

const notSavedStates = [
  EntityStateConstants.CREATING,
  EntityStateConstants.UPDATING,
  EntityStateConstants.DELETING,
];

interface ServiceMap {
  [serviceType: string]: {
    endTime: string;
    startTime: string;
  };
}

const defaultValues: ServiceMap = {
  BREAKFAST: {
    startTime: '08:00',
    endTime: '11:00',
  },
  BRUNCH: {
    startTime: '09:00',
    endTime: '12:00',
  },
  LUNCH: {
    startTime: '11:00',
    endTime: '13:00',
  },
  DINNER: {
    startTime: '17:00',
    endTime: '23:00',
  },
};

function getSaveButtonState(state: State) {
  const needsSaving = _.some(_.flatten(_.valuesIn(state.forWeekday)), (service) =>
    _.includes(notSavedStates, service.state)
  );
  return needsSaving ? SaveButtonConstants.NOT_SAVED : SaveButtonConstants.SAVED;
}

function getServiceById(services: any[], id: number) {
  return _.find(services, (s) => s.id === id);
}

export default function reducer(state = initialState, action: BaseTockAction): State {
  const { type, payload, context } = action;
  const forWeekday = _.cloneDeep(state.forWeekday);
  switch (type) {
    case AppConstants.SERVICES_CREATE: {
      const weekday = payload;

      let possibleServices = ['DINNER', 'LUNCH', 'BRUNCH', 'BREAKFAST'];
      possibleServices = _.without(
        possibleServices,
        ..._.map(forWeekday[weekday], (service) => service.serviceType)
      );
      const serviceType = _.first(possibleServices) || 'DINNER';

      const lastService = _.find(
        _.reverse(_.flatMap(forWeekday)),
        (service) => service.serviceType === serviceType
      );
      const defaultValue = defaultValues[serviceType];

      forWeekday[weekday].push({
        id: null as any,
        weekday,
        startTime: oc(lastService).startTime(defaultValue.startTime),
        endTime: oc(lastService).endTime(defaultValue.endTime),
        serviceType: serviceType as any,
        state: EntityStateConstants.CREATING,
      });
      forWeekday[weekday] = _.sortBy(forWeekday[weekday], 'startTime');

      return {
        ...state,
        forWeekday,
        saveButtonState: SaveButtonConstants.NOT_SAVED,
      };
    }
    case AppConstants.SERVICES_DELETE: {
      const deletedService = payload.service;
      const index = payload.index;
      const weekday = deletedService.weekday;
      const service = forWeekday[weekday][index];

      // If it has an id, we need to commit it to the db, so mark it as DELETING and that saving is needed
      if (service.id) {
        service.state = EntityStateConstants.DELETING;
      } else {
        // Otherwise, just remove it from the list and update the save button state accordingly
        forWeekday[weekday].splice(index, 1);
      }
      const newState = {
        ...state,
        forWeekday,
      };
      return {
        ...newState,
        saveButtonState: service.id ? SaveButtonConstants.NOT_SAVED : getSaveButtonState(newState),
      };
    }

    case AppConstants.SERVICES_UPDATE: {
      const serviceChange = payload.serviceChange;
      const weekday = serviceChange.weekday;
      const index = payload.index;
      const service = forWeekday[weekday][index];

      if (serviceChange.serviceType) {
        const lastService = _.find(_.reverse(_.flatMap(forWeekday)), {
          serviceType: serviceChange.serviceType,
        }) as core.Service;
        const defaultValue = defaultValues[serviceChange.serviceType];

        _.assign(service, {
          startTime: oc(lastService).startTime(defaultValue.startTime),
          endTime: oc(lastService).endTime(defaultValue.endTime),
        });
      }

      _.assign(service, serviceChange);

      if (service.state === EntityStateConstants.SAVED) {
        service.state = EntityStateConstants.UPDATING;
        state.saveButtonState = SaveButtonConstants.NOT_SAVED;
      }
      return {
        ...state,
        forWeekday,
      };
    }

    case AppConstants.SERVICES_CREATE_DONE: {
      const service = toJSON<core.Service>(payload);
      const servicesForDay = forWeekday[service.weekday];
      let pendingService = _.find(
        servicesForDay,
        (s) =>
          _.isNull(s.id) &&
          service.startTime === s.startTime &&
          service.endTime === s.endTime &&
          service.serviceType === s.serviceType
      );
      pendingService = _.assign(pendingService, service);
      pendingService.state = EntityStateConstants.SAVED;
      const newState = {
        ...state,
        forWeekday,
      };
      return {
        ...newState,
        saveButtonState: getSaveButtonState(newState),
      };
    }
    case AppConstants.SERVICES_DELETE_DONE: {
      const deletedService = context.deletedService;
      const weekday = deletedService.weekday;
      const service = getServiceById(forWeekday[weekday], deletedService.id);

      // Sanity check / allow for rollbacks
      if (service.state === EntityStateConstants.DELETING) {
        forWeekday[weekday] = _.without(forWeekday[weekday], service);
      }
      const newState = {
        ...state,
        forWeekday,
      };
      return {
        ...newState,
        saveButtonState: getSaveButtonState(newState),
      };
    }
    case AppConstants.SERVICES_UPDATE_DONE: {
      const updatedService = toJSON<core.Service>(payload);
      const weekday = updatedService.weekday;
      const service = getServiceById(forWeekday[weekday], updatedService.id);
      service.state = EntityStateConstants.SAVED;
      const newState = {
        ...state,
        forWeekday,
      };
      return {
        ...newState,
        saveButtonState: getSaveButtonState(newState),
      };
    }
    case AppConstants.SERVICES_CREATE_STARTING:
    case AppConstants.SERVICES_DELETE_STARTING:
    case AppConstants.SERVICES_UPDATE_STARTING: {
      return {
        ...state,
        saveButtonState: SaveButtonConstants.SAVING,
      };
    }
    case AppConstants.SERVICES_CREATE_FAILED:
    case AppConstants.SERVICES_DELETE_FAILED:
    case AppConstants.SERVICES_UPDATE_FAILED: {
      return {
        ...state,
        saveButtonState: SaveButtonConstants.NOT_SAVED,
      };
    }
    case AppConstants.SERVICES_FETCH_ALL_STARTING: {
      return {
        ...initialState,
        isInitialized: state.isInitialized,
        requestInProgress: true,
      };
    }
    case AppConstants.SERVICES_FETCH_ALL_DONE: {
      const services = payload.map((serviceMessage: core.Service) => {
        const service = toJSON<core.Service>(serviceMessage);
        (service as ExtendedService).state = EntityStateConstants.SAVED;
        return service;
      });
      _.assign(
        forWeekday,
        _.groupBy(services, (service) => service.weekday)
      );
      _.forEach(_.keys(forWeekday), (weekday) => {
        forWeekday[weekday] = _.sortBy(forWeekday[weekday], 'startTime');
      });
      const newState = {
        ...state,
        forWeekday,
        isInitialized: true,
      };
      return {
        ...newState,
        saveButtonState: getSaveButtonState(newState),
        requestInProgress: false,
      };
    }

    default:
      return state;
  }
}
